import { useEffect, useState } from "react";
import UserData from "../Controls/UserData";
import { Form, FormInput, FormActions } from "../Controls/Form";
import CustomButton from "../Controls/CustomButton";
import Helpers from "../Controls/helpers";
import API from "@src/API.js";
import { toast } from "react-toastify";
import { useAccountProfileQuery } from "../../query/useAccountProfile";
import { useSessionShellQuery } from "../../query/useSessionShell";

function emptyContactInfo() {
  return {
    ID: 0,
    Name: "",
    Phone: "",
    Email: "",
  };
}

/**
 * Contact info form body. Prefer ContactInfoEditModal for UI entry points.
 */
export default function ContactInfoEdit({ contactInfoId = 0, onClose }) {
  const id = Number.parseInt(contactInfoId, 10) || 0;
  const { data: profile } = useAccountProfileQuery();
  const { data: shell } = useSessionShellQuery();
  const [contactInfo, updateContactInfo] = useState(() => emptyContactInfo());
  const [smsBusy, setSmsBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const isSysAdmin = Helpers.isSysAdmin(shell || {});

  useEffect(() => {
    if (!id) {
      updateContactInfo(emptyContactInfo());
      return;
    }
    const found = (profile?.contactInfos || []).find((i) => Number(i.ID ?? i.id) === id);
    if (found) updateContactInfo({ ...found });
  }, [id, profile]);

  function close() {
    onClose?.();
  }

  async function onDoneAsync() {
    const ret = await UserData.UpdateAsync("contactInfos", contactInfo, true);
    if (ret && ret.result) {
      toast.success("Contact info saved");
      close();
    }
  }

  async function onDelete() {
    const deleteId = contactInfo.ID ?? contactInfo.id;
    const resp = await UserData.DeleteAsync("contactInfos", deleteId);
    if (resp && resp.result) {
      toast.success("Contact info deleted");
      close();
    }
  }

  async function sendTestSms() {
    const phone = (contactInfo.Phone || "").trim();
    if (!phone || smsBusy) return;
    setSmsBusy(true);
    try {
      const resp = await API.postAsync("/ContactInfo/TestSms", { phone });
      if (resp?.ok) {
        toast.success(resp?.jsonData?.message || "Test SMS queued");
      } else {
        toast.error(resp?.jsonData?.message || "Could not send test SMS");
      }
    } catch (err) {
      toast.error(err?.message || "Could not send test SMS");
    } finally {
      setSmsBusy(false);
    }
  }

  async function sendTestEmail() {
    const email = (contactInfo.Email || "").trim();
    if (!email || emailBusy) return;
    setEmailBusy(true);
    try {
      const resp = await API.postAsync("/ContactInfo/TestEmail", { email });
      if (resp?.ok) {
        toast.success(resp?.jsonData?.message || "Test email queued");
      } else {
        toast.error(resp?.jsonData?.message || "Could not send test email");
      }
    } catch (err) {
      toast.error(err?.message || "Could not send test email");
    } finally {
      setEmailBusy(false);
    }
  }

  const phoneTrimmed = (contactInfo.Phone || "").trim();
  const emailTrimmed = (contactInfo.Email || "").trim();

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={contactInfo} onUpdate={updateContactInfo}>
        <FormInput label="Place Name" field="Name" />
        <FormInput label="Phone" field="Phone" />
        {isSysAdmin ? (
          <div className="field form-group" style={{ marginTop: "-0.5rem" }}>
            <CustomButton
              type="button"
              variant="outlined"
              color="secondary"
              disabled={!phoneTrimmed || smsBusy}
              onClick={sendTestSms}
              data-testid="contact-info-test-sms"
            >
              {smsBusy ? "Sending…" : "Send test SMS"}
            </CustomButton>
          </div>
        ) : null}
        <FormInput label="Email" field="Email" />
        {isSysAdmin ? (
          <div className="field form-group" style={{ marginTop: "-0.5rem" }}>
            <CustomButton
              type="button"
              variant="outlined"
              color="secondary"
              disabled={!emailTrimmed || emailBusy}
              onClick={sendTestEmail}
              data-testid="contact-info-test-email"
            >
              {emailBusy ? "Sending…" : "Send test email"}
            </CustomButton>
          </div>
        ) : null}
        <FormActions
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            (contactInfo.ID ?? contactInfo.id) > 0
              ? async () => {
                  await onDelete();
                }
              : undefined
          }
        />
      </Form>
    </div>
  );
}
